import * as yup from 'yup'

export const loginWithoutPasswordSchema = yup.object().shape(
  {
    email: yup
      .string()
      .email('Please enter a valid email address')
      .when('phoneNumber', {
        is: phoneNumber => !phoneNumber || phoneNumber.length === 0,
        then: schema => schema.required('Email or phone number is required'),
        otherwise: schema => schema.notRequired(),
      }),
    phoneNumber: yup
      .string()
      .test(
        'phone-format',
        'Please enter a valid 10-digit phone number',
        value => {
          if (!value) return true
          const digits = value.replace(/\D/g, '')
          return digits.length === 10
        }
      )
      .when('email', {
        is: email => !email || email.length === 0,
        then: schema => schema.required('Email or phone number is required'),
        otherwise: schema => schema.notRequired(),
      }),
  },
  [['email', 'phoneNumber']]
)

export const accountRecoverySchema = yup.object().shape(
  {
    email: yup
      .string()
      .transform(value => (value === '' ? undefined : value))
      .email('Please enter a valid email address')
      .when('phoneNumber', {
        is: phoneNumber => !phoneNumber || phoneNumber.length === 0,
        then: schema => schema.required('Email or phone number is required'),
        otherwise: schema => schema.notRequired(),
      }),
    newEmail: yup
      .string()
      .transform(value => (value === '' ? undefined : value))
      .email('Please enter a valid email address')
      .notRequired(),
    phoneNumber: yup
      .string()
      .test(
        'phone-format',
        'Please enter a valid 10-digit phone number',
        value => {
          if (!value) return true
          const digits = value.replace(/\D/g, '')
          return digits.length === 10
        }
      )
      .when('email', {
        is: email => !email || email.length === 0,
        then: schema => schema.required('Email or phone number is required'),
        otherwise: schema => schema.notRequired(),
      }),
    birthdate: yup
      .string()
      .test('valid-date', 'Please enter a valid date', value => {
        if (!value) return true
        const date = new Date(value)
        return !isNaN(date.getTime())
      })
      .test('not-in-future', 'Birthdate cannot be in the future', value => {
        if (!value) return true
        const date = new Date(value)
        if (isNaN(date.getTime())) return true
        const today = new Date()
        // Compare dates at day precision
        const dateOnly = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        )
        const todayOnly = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        )
        return dateOnly <= todayOnly
      })
      .notRequired(),
    donorNumber: yup.string().notRequired(),
  },
  [['email', 'phoneNumber']]
)

/**
 * Creates a validation schema for guest appointment confirmation
 * @param {boolean} isEmailRequired - Whether email is required based on tenant configuration
 * @param {boolean} isPhoneRequired - Whether phone number is required based on tenant configuration
 * @param {boolean} isEmailOrPhoneRequired - Whether either email or phone is required (at least one)
 * @returns {yup.ObjectSchema} - The validation schema
 */
export const createGuestAppointmentSchema = (
  isEmailRequired = false,
  isPhoneRequired = false,
  isEmailOrPhoneRequired = false
) => {
  return yup.object().shape(
    {
      firstName: yup.string().required('First name is required').trim(),
      lastName: yup.string().required('Last name is required').trim(),
      email: yup
        .string()
        .transform(value => (value === '' ? undefined : value))
        .email('Please enter a valid email address')
        .when('phoneNumber', {
          is: phoneNumber => !phoneNumber || phoneNumber.length === 0,
          then: schema => {
            // If email is individually required, always require it
            if (isEmailRequired) {
              return schema.required('Email is required')
            }
            // If either/or is required and phone is not individually required, require email when phone is empty
            if (isEmailOrPhoneRequired && !isPhoneRequired) {
              return schema.required(
                'Please provide either an email address or phone number'
              )
            }
            return schema.notRequired()
          },
          otherwise: schema => schema.notRequired(),
        }),
      phoneNumber: yup
        .string()
        .transform(value => (value === '' ? undefined : value))
        .test(
          'phone-format',
          'Please enter a valid 10-digit phone number',
          value => {
            if (!value) return true
            const digits = value.replace(/\D/g, '')
            return digits.length === 10
          }
        )
        .when('email', {
          is: email => !email || email.length === 0,
          then: schema => {
            // If phone is individually required, always require it
            if (isPhoneRequired) {
              return schema.required('Phone number is required')
            }
            // If either/or is required and email is not individually required, require phone when email is empty
            if (isEmailOrPhoneRequired && !isEmailRequired) {
              return schema.required(
                'Please provide either an email address or phone number'
              )
            }
            return schema.notRequired()
          },
          otherwise: schema => schema.notRequired(),
        }),
    },
    [['email', 'phoneNumber']]
  )
}
